import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { Interval } from 'types/interfaces/Interval';

import { TaskService } from 'components/services/TaskService';
import { DateTimeInput } from 'ui/custom-controls/DateTimeInput';
import type { CalendarOnChangeEvent } from 'ui/custom-controls/DateTimeInput';
import IntervalPickerDialog from 'ui/custom-controls/IntervalPickerDialog';

import { getPriorityClass } from '../../pages/tasks/helpers';
import {
  DropdownObjectType,
  Employee,
  FieldFilling,
  Task,
  TreeNode
} from '../../types/interfaces';
import { getEmployeePickerEmptyMessage, PickerTexts } from '../../utils';
import { formatDateStringFromRaw } from '../../utils/date-utils';
import { getFieldNameSchema } from '../../utils/getFieldName';
import { CustomDropdown } from '../custom-controls';
import ConfirmationDialog from '../shared/ConfirmationDialog';

import { AssignedExecutorsTags } from './AssignedExecutorsTags';
import { AutoAssignmentDialog } from './AutoAssignmentDialog';
import { AutoAssignmentInfoDialog } from './AutoAssignmentInfoDialog';
import { ExecutorSkillsCell } from './ExecutorSkillsCell';
import { TaskActionsSection } from './TaskActionsSection';
import { TaskCardAutoEmployeeAssignmentDialog } from './TaskCardAutoEmployeeAssignmentDialog';
import { TaskFieldRow } from './TaskFieldRow';

interface MainTabProps {
  fieldFillingControll: FieldFilling[];
  creationDate: string;
  errors: FieldErrors;
  taskId: string | number;
  taskTypes: DropdownObjectType[];
  taskType: DropdownObjectType | null;
  assetOptions: DropdownObjectType[];
  asset: TreeNode | null;
  setSelectedPriority: React.Dispatch<
    React.SetStateAction<DropdownObjectType | null>
  >;
  department: TreeNode | null;
  departmentsDropDownValue: TreeNode[];
  fullDepartmentsTree: TreeNode[];
  fullExecutorsCatalog: Employee[];
  handleSelectionChange: (e: any) => void;
  handleDistrictSelectionChange: (e: any) => void;
  handleCreateClick: () => void;
  employees: Employee[];
  executorsTable: Employee[];
  selectedPriority: DropdownObjectType | null;
  PriorityOptions: DropdownObjectType[];
  watch: (field: string) => any;
  closeCode: { taskClosingCode: string } | null;
  handleActionSelect: (id: number) => void;
  possibleStatusTransitions?: { id: number; label: string }[];
  selectedAction: any;
  handleStartDateChange: (e: any) => void;
  handlePlannedStartDateChange: (e: any) => void;
  actualStartDateRef: RefObject<any>;
  CalendarHideHandle: (ref: RefObject<any>) => void;
  handleEndDateChange: (
    e: CalendarOnChangeEvent,
    field: 'plannedEndDate' | 'actualEndDate'
  ) => Promise<void>;
  actualEndDateRef: RefObject<any>;
  setValue: UseFormSetValue<Task>;
  fetchTaskTypeDetails: (taskId: number) => Promise<void>;
  plannedEndDateRef: RefObject<any>;
  loading: boolean;
  register: UseFormRegister<Task>;
  executorNodes: any[];
  handleDeleteTag: (tag: any) => void;
  handleDataTableDropdownSelectionChange: (
    e: Employee[],
    options?: { markDirty?: boolean }
  ) => void;
  recommendedEmployeesCount: number | null;
  showAllDepartments: boolean;
  toggleShowAllDepartments: () => void;
  departmentPickerError: string | null;
  employeePickerError: string | null;
  onToggleShowAll?: () => void;
  showAllEmployees?: boolean;
  isDepartmentDeleted: boolean;
  isDirty: boolean;
  isDepartmentDirty: boolean;
  setIsDepartmentDeleted: (deleted: boolean) => void;
  hasRouteAssignment?: boolean;
}

const formatIntervalDisplay = (interval: any | null) => {
  if (!interval) return '';
  
  const startDate = new Date(interval.start);
  const endDate = new Date(interval.end);
  
  const formatDateTime = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
    return `${day}.${month}.${year} ${time}`;
  };
  
  return `${formatDateTime(startDate)} - ${formatDateTime(endDate)}`;
};

const defaultNode: TreeNode = { key: '', label: '' };

export const MainTab: React.FC<MainTabProps> = ({
  fieldFillingControll,
  creationDate,
  errors,
  taskId,
  taskTypes,
  taskType,
  assetOptions,
  asset,
  setSelectedPriority,
  department,
  departmentsDropDownValue,
  fullDepartmentsTree,
  fullExecutorsCatalog,
  handleSelectionChange,
  handleDistrictSelectionChange,
  handleCreateClick,
  employees,
  executorsTable,
  selectedPriority,
  PriorityOptions,
  watch,
  closeCode,
  handleActionSelect,
  possibleStatusTransitions,
  selectedAction,
  handleStartDateChange,
  handlePlannedStartDateChange,
  actualStartDateRef,
  CalendarHideHandle,
  handleEndDateChange,
  actualEndDateRef,
  plannedEndDateRef,
  loading,
  setValue,
  fetchTaskTypeDetails,
  register,
  executorNodes,
  handleDeleteTag,
  handleDataTableDropdownSelectionChange,
  recommendedEmployeesCount,
  showAllDepartments,
  toggleShowAllDepartments,
  departmentPickerError,
  employeePickerError,
  onToggleShowAll,
  showAllEmployees,
  isDepartmentDeleted,
  setIsDepartmentDeleted,
  isDirty,
  isDepartmentDirty,
  hasRouteAssignment
}) => {
  const originalEmployeesRef = useRef<Employee[]>([]);
  const [showTaskTypeChangeDialog, setShowTaskTypeChangeDialog] = useState(false);
  const [showIntervalDialog, setShowIntervalDialog] = useState(false);
  const [newSelectedTaskType, setNewSelectedTaskType] = useState<TreeNode | null>(null);
  const [showActionConfirmationDialog, setShowActionConfirmationDialog] = useState(false);
  const [autoAssignmentResponse, setAutoAssignmentResponse] = useState<any>(null);
  const [openAutoDepartmentAssignmentDialog, setOpenAutoDepartmentAssignmentDialog] = useState(false);
  const [showAutoAssignmentInfoDialog, setShowAutoAssignmentInfoDialog] = useState(false);
  const [autoEmployeeResponse, setAutoEmployeeResponse] = useState<any>(null);
  const [openAutoEmployeeAssignmentDialog, setOpenAutoEmployeeAssignmentDialog] = useState(false);
  const [showSaveDepartmentNotice, setShowSaveDepartmentNotice] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<number | null>(null);
  const isImported = watch('isImported');
  const isClosed = watch('isClosed');
  const selectedInterval = watch('interval') || null;


  const handleIntervalSelect = (interval: Interval | null) => {
    if (interval) {
      setValue('interval', interval, {
        shouldDirty: true,
        shouldValidate: true
      });
      setValue('plannedStartDate', interval.start, {
        shouldDirty: true,
        shouldValidate: false
      });
      setValue('plannedEndDate', interval.end, {
        shouldDirty: true,
        shouldValidate: true
      });
    } else {
      setValue('interval', undefined, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  };

  const handleClearInterval = () => {
    setValue('interval', undefined, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const hasTaskTypeForAutoAssign = Boolean(
    taskType && String(taskType.key ?? '').trim() !== ''
  );

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);


  const handleTaskTypeChangeAndResetIntervals = useCallback(async (selectedTaskType: TreeNode) => {

    setValue('interval', undefined, {
      shouldDirty: true,
      shouldValidate: true
    });

    if (creationDate && taskType) {
      setNewSelectedTaskType(selectedTaskType);
      setShowTaskTypeChangeDialog(true);
    } else {
      const newTaskType = selectedTaskType ?? defaultNode;
      setValue('taskType', newTaskType, {
        shouldDirty: true,
        shouldValidate: true
      });

      if (selectedTaskType) {
        fetchTaskTypeDetails(Number(selectedTaskType.key));
      }
    }
  }, [creationDate, taskType, setValue, fetchTaskTypeDetails]);

  const handleDistrictChangeWithReset = useCallback((e: any) => {
    setValue('interval', undefined, {
      shouldDirty: true,
      shouldValidate: true
    });
    
    handleDistrictSelectionChange(e);
  }, [handleDistrictSelectionChange, setValue]);

  const executorsAvailableForDeletedMark = fullExecutorsCatalog.length
    ? fullExecutorsCatalog
    : executorsTable;

  const isFieldDisabled = (fieldName: string) => {
    const field = fieldFillingControll.find(
      (f) => getFieldNameSchema(f.fieldType) === fieldName
    );
    return (
      field?.currentFunctions.some((func) => func.id.toString() === '2') ??
      false
    );
  };

  const handleAssign = useCallback(async () => {
    try {
      const response = await TaskService.AutoTaskAssignment(
        [Number(taskId)],
        true
      );
      setAutoAssignmentResponse(response.data);
      setOpenAutoDepartmentAssignmentDialog(true);
    } catch (error) {
      console.error('Ошибка при автоматическом подборе участка:', error);
    }
  }, [taskId]);

  const possibleStatusTransitionsHandle = (id: number) => {
    if (isClosed) {
      setPendingActionId(id);
      setShowActionConfirmationDialog(true);
    } else {
      handleActionSelect(id);
    }
  };

  const handleConfirmationContinue = () => {
    if (pendingActionId !== null) {
      handleActionSelect(pendingActionId);
    }
    setShowActionConfirmationDialog(false);
    setPendingActionId(null);
  };

  const handleConfirmationCancel = () => {
    setShowActionConfirmationDialog(false);
    setPendingActionId(null);
  };

  const renderExternalId = () => {
    return (
      <TaskFieldRow
        htmlFor="externalId"
        labelVariant="col4"
        reserveErrorSpace
        label={
          <>
            Внешний №
            {isImported && (
              <>
                <i
                  data-pr-tooltip="Данный наряд был импортирован."
                  data-pr-position="top"
                  className="ml-2 pi pi-download mr-2"
                ></i>
              </>
            )}
            :
          </>
        }
      >
        {isImported && <Tooltip target="[data-pr-tooltip]" />}
        <InputText
          className="mr-2 ml-2 col task-card__field-input"
          id="externalId"
          {...register('externalId', {
            onChange: (e) => {
              setValue('externalId', e.target.value, {
                shouldDirty: true,
                shouldValidate: true
              });
            }
          })}
          disabled={isFieldDisabled('externalId')}
        />
      </TaskFieldRow>
    );
  };

  const getEmptyMessage = () => {
    if (employeePickerError) {
      return employeePickerError;
    }

    return getEmployeePickerEmptyMessage({
      hasDepartment: Boolean(department?.key),
      showAllEmployees: showAllEmployees ?? true,
      currentEmployeesCount: executorsTable.length,
      allDepartmentEmployeesCount: fullExecutorsCatalog.length
    });
  };

  useEffect(() => {
    if (employees?.length && originalEmployeesRef.current.length === 0) {
      originalEmployeesRef.current = JSON.parse(JSON.stringify(employees));
    }
  }, [employees]);

  const isEmployeesDirty = (() => {
    const currentEmployees = watch('employees');
    const originalEmployeesCount = originalEmployeesRef.current.length;
    const currentEmployeesCount = currentEmployees?.length || 0;

    if (originalEmployeesCount !== currentEmployeesCount) {
      return true;
    }

    return (
      JSON.stringify(currentEmployees) !==
      JSON.stringify(originalEmployeesRef.current)
    );
  })();

  const showAutoAssignButton =
    !!department?.key &&
    !!taskId &&
    hasTaskTypeForAutoAssign &&
    (employees?.length === 0 || (isEmployeesDirty && employees?.length === 0));

  const showAssignInfoButton =
    employees?.length > 0 && hasRouteAssignment && !isEmployeesDirty;

  const handleAssignEmployees = useCallback(async () => {
    if (isDepartmentDirty || isEmployeesDirty) {
      setAutoEmployeeResponse(null);
      setShowSaveDepartmentNotice(true);
      setOpenAutoEmployeeAssignmentDialog(true);
      return;
    }

    try {
      const response = await TaskService.AutoTaskEmployeeAssignment(
        [Number(taskId)],
        true
      );
      setShowSaveDepartmentNotice(false);
      setAutoEmployeeResponse(response.data);
      setOpenAutoEmployeeAssignmentDialog(true);
    } catch (error) {
      console.error('Ошибка при автоматическом подборе исполнителей:', error);
    }
  }, [isDepartmentDirty, isEmployeesDirty, taskId]);

  const isDepartmentInterval = (departmentId: string): boolean => {
    if (!departmentId) return false;

    const findNode = (nodes: TreeNode[], key: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findNode(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    const foundNode = findNode(departmentsDropDownValue, departmentId);
    return (foundNode as any)?.isIntervalType === true;
  };

  const isInterval = isDepartmentInterval(department?.key as string);

  const showIntervalField = 
    department?.key && 
    taskType?.key && 
    isInterval

  
  return (
    <div className="mainTab-task-card">
      {loading ? (
        <ProgressSpinner />
      ) : (
        <>
          <ConfirmationDialog
            visible={showTaskTypeChangeDialog}
            onHide={() => setShowTaskTypeChangeDialog(false)}
            onCancel={() => setShowTaskTypeChangeDialog(false)}
            onContinue={() => {
              const newTaskType = newSelectedTaskType ?? defaultNode;
              setValue('taskType', newTaskType, {
                shouldDirty: true,
                shouldValidate: true
              });
              newSelectedTaskType &&
                fetchTaskTypeDetails(Number(newSelectedTaskType?.key));
              setShowTaskTypeChangeDialog(false);
            }}
            header="Подтверждение изменения типа наряда"
            dialogStyle={{
              minWidth: isMobile ? '85vw' : '743px',
              width: isMobile ? '85vw' : '743px',
              minHeight: '274px'
            }}
            confirmLabel="Подтвердить"
            cancelLabel="Отмена"
            confirmIcon="pi pi-check"
            cancelIcon="pi pi-times"
            footerClassName="flex gap-3"
            icon={
              <i
                className="pi pi-exclamation-triangle mr-3"
                style={{ fontSize: '2rem' }}
              />
            }
            message="Вы уверены, что хотите изменить тип наряда? Плановая дата и время завершения работы, а также плановые работы могут быть изменены."
          />

          <AutoAssignmentDialog
            visible={openAutoDepartmentAssignmentDialog}
            response={autoAssignmentResponse}
            onHide={() => setOpenAutoDepartmentAssignmentDialog(false)}
            taskId={Number(taskId)}
            onDistrictChange={(departmentData) => {
              handleDistrictSelectionChange(departmentData);
            }}
            isDepartmentDeleted={isDepartmentDeleted}
            isDirty={isDirty}
          />

          <AutoAssignmentInfoDialog
            visible={showAutoAssignmentInfoDialog}
            onHide={() => setShowAutoAssignmentInfoDialog(false)}
            taskId={Number(taskId)}
          />

          <TaskCardAutoEmployeeAssignmentDialog
            visible={openAutoEmployeeAssignmentDialog}
            onHide={() => {
              setOpenAutoEmployeeAssignmentDialog(false);
              setShowSaveDepartmentNotice(false);
              setAutoEmployeeResponse(null);
            }}
            response={autoEmployeeResponse}
            taskId={taskId}
            showSaveDepartmentNotice={showSaveDepartmentNotice}
            onApplyAssignment={(suggested) => {
              handleDataTableDropdownSelectionChange(suggested, {
                markDirty: false
              });
            }}
          />

          <ConfirmationDialog
            visible={showActionConfirmationDialog}
            onHide={handleConfirmationCancel}
            onCancel={handleConfirmationCancel}
            onContinue={handleConfirmationContinue}
            header="Подтверждение действия"
            confirmLabel="Продолжить"
            cancelLabel="Отмена"
            confirmIcon="pi pi-check"
            cancelIcon="pi pi-times"
            footerClassName="flex gap-3"
            message="Наряд является закрытым, совершая данный переход наряд будет возвращен в работу"
          />

          <div className="grid" style={{ marginTop: 1 }}>
            <div className="col task-card__column flex flex-column mr-4">
              {creationDate && (
                <TaskFieldRow
                  htmlFor="creationDate"
                  spaced
                  label="Дата и время регистрации наряда:"
                  hasError={!!errors.creationDate}
                  errorMessage={errors.creationDate?.message as React.ReactNode}
                >
                  <InputText
                    className="col mr-2 ml-2 task-card__field-input"
                    value={formatDateStringFromRaw(creationDate)}
                  />
                </TaskFieldRow>
              )}

              <TaskFieldRow
                htmlFor="name"
                label="Название наряда:"
                hasError={!!errors.name}
                errorMessage={errors.name?.message as React.ReactNode}
              >
                <InputText
                  id="name"
                  className="col mr-2 ml-2 task-card__field-input"
                  {...register('name', {
                    onChange: (e) => {
                      setValue('name', e.target.value, {
                        shouldDirty: true,
                        shouldValidate: true
                      });
                    }
                  })}
                  disabled={isFieldDisabled('name')}
                />
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="taskType"
                label={
                  <>
                    Тип наряда<span className="required">*</span>:
                  </>
                }
                hasError={!!errors.taskType}
                errorMessage={(errors.taskType as any)?.message}
              >
                <div className="col">
                  <CustomDropdown
                    assetOptions={taskTypes}
                    selectedAsset={taskType}
                    onSelectionChange={(e) => {
                      const selectedTaskType = e as TreeNode;
                      handleTaskTypeChangeAndResetIntervals(selectedTaskType);
                    }}
                    disabled={isFieldDisabled('taskType')}
                    placeholder="Выберите тип наряда"
                    showClearOnHover
                    showDeletedWarning
                    deletedWarningTooltip="Тип наряда удалён в справочнике"
                  />
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="priority"
                label="Приоритет:"
                hasError={!!errors.priority}
                errorMessage={errors.priority?.message as React.ReactNode}
              >
                <div className="col">
                  <CustomDropdown
                    assetOptions={PriorityOptions}
                    selectedAsset={selectedPriority}
                    setSelectedAsset={(e) => {
                      const key = (e as DropdownObjectType)?.key ?? 0;
                      const label =
                        (e as DropdownObjectType)?.label ?? 'Низкий';
                      const priorityValue = parseInt(key.toString());

                      setValue('priority', priorityValue, {
                        shouldDirty: true,
                        shouldValidate: true
                      });
                      setSelectedPriority({
                        label,
                        key: priorityValue
                      } as DropdownObjectType);
                    }}
                    placeholder="Выберите приоритет"
                    passedClassName={getPriorityClass(selectedPriority)}
                    disabled={isFieldDisabled('priority')}
                    showClearOnHover
                  />
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="asset"
                label="Объект:"
                hasError={!!errors.asset}
                errorMessage={errors.asset?.message as React.ReactNode}
              >
                <div className="col">
                  <CustomDropdown
                    assetOptions={assetOptions}
                    handleCreateClick={handleCreateClick}
                    selectedAsset={asset}
                    onSelectionChange={handleSelectionChange}
                    placeholder="Выберите объект"
                    disabled={isFieldDisabled('asset')}
                    showClearOnHover
                    createButtonTooltip="Создать новый объект"
                    showDeletedWarning
                    deletedWarningTooltip="Объект удалён или неактивен"
                    style={{height: 42}}
                  />
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="department"
                label="Участок:"
                hasError={!!errors.department}
                errorMessage={errors.department?.message as React.ReactNode}
              >
                <div className="col flex gap-3">
                  <CustomDropdown
                    key="department"
                    assetOptions={[]}
                    treeValue={departmentsDropDownValue}
                    useTree={true}
                    selectedAsset={department}
                    onTreeSelectionChange={handleDistrictChangeWithReset}
                    placeholder="Выберите участок"
                    disabled={isFieldDisabled('department')}
                    showClearOnHover
                    showToggleButton
                    onToggleShowAll={toggleShowAllDepartments}
                    showAll={showAllDepartments}
                    toggleShowAllTooltip="Показать все участки системы"
                    toggleShowFilteredTooltip="Показать подходящие участки"
                    showDeletedWarning
                    deletedWarningTooltip="Участок удалён в справочнике"
                    deletedWarningCheckTreeValue={fullDepartmentsTree}
                    departmentDropdown={true}
                    emptyMessage={departmentPickerError ?? undefined}
                    assetId={asset?.key ? Number(asset.key) : undefined}
                    setIsDepartmentDeleted={setIsDepartmentDeleted}
                  />
                  {asset?.key && taskType?.key && department?.key === '' && (
                    <Button
                      className="functional-structure-save-btn__roles"
                      icon="pi pi-sync"
                      tooltip="Автоматически подобрать участок для наряда"
                      tooltipOptions={{ position: 'top' }}
                      onClick={handleAssign}
                      style={{ minWidth: 42 }}
                    />
                  )}
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="employees"
                hasError={!!errors.employees}
                errorMessage={errors.employees?.message as React.ReactNode}
                label={
                  <>
                    Исполнители:
                    {recommendedEmployeesCount !== null && (
                      <>
                        <i
                          className="pi pi-info-circle ml-2 recommended-employees-tooltip"
                          data-pr-tooltip={`Рекомендованное количество исполнителей: ${recommendedEmployeesCount}`}
                          data-pr-position="top"
                          style={{
                            color: 'rgb(59 130 246)',
                            cursor: 'pointer'
                          }}
                        />
                        <Tooltip target=".recommended-employees-tooltip" />
                      </>
                    )}
                  </>
                }
              >
                <div className="col flex gap-3">
                  <CustomDropdown<Employee>
                    key="employees"
                    multiplySelectMode
                    assetOptions={executorsTable}
                    selectedAsset={employees}
                    onSelectionChange={(e) =>
                      handleDataTableDropdownSelectionChange(e as Employee[])
                    }
                    placeholder={PickerTexts.executors(employees?.length || 0)}
                    columns={[
                      {
                        field: 'personnelNumber',
                        header: 'Табельный номер',
                        width: '22%'
                      },
                      { field: 'fullName', header: 'ФИО', width: '30%' },
                      {
                        field: 'id',
                        header: 'Навыки для плановых работ',
                        width: '45%',
                        body: (row: any) => <ExecutorSkillsCell row={row} />
                      }
                    ]}
                    customHeight={300}
                    emptyMessage={getEmptyMessage()}
                    disabled={isFieldDisabled('employees')}
                    showClearOnHover
                    overlayWidthMultiplier={1.3}
                    showToggleButton={true}
                    onToggleShowAll={onToggleShowAll}
                    showAll={showAllEmployees}
                    clearButtonTooltip="Сбросить выбор исполнителей"
                    toggleShowAllTooltip="Показать всех сотрудников"
                    toggleShowFilteredTooltip="Показать только подходящих сотрудников"
                    filterWithLabel
                    showDeletedWarning
                    deletedWarningTooltip="Некоторые исполнители удалены в справочнике"
                    deletedWarningCheckAssetOptions={fullExecutorsCatalog}
                  />
                  {showAutoAssignButton && (
                    <Button
                      className="functional-structure-save-btn__roles"
                      icon="pi pi-sync"
                      tooltip="Автоматически подобрать исполнителей для наряда"
                      tooltipOptions={{ position: 'top' }}
                      onClick={handleAssignEmployees}
                      disabled={isFieldDisabled('employees')}
                      style={{ minWidth: 42 }}
                    />
                  )}
                  {showAssignInfoButton && (
                    <Button
                      icon="pi pi-question"
                      tooltip="Посмотреть информацию о распределении"
                      tooltipOptions={{ position: 'top' }}
                      onClick={() =>
                        setShowAutoAssignmentInfoDialog(
                          !showAutoAssignmentInfoDialog
                        )
                      }
                      style={{ minWidth: 42 }}
                    />
                  )}
                </div>
              </TaskFieldRow>
              {showIntervalField && (
                <TaskFieldRow
                  htmlFor="interval"
                  label="Интервал:"
                  hasError={!!errors.interval}
                  errorMessage={errors.interval?.message as React.ReactNode}
                >
                  <div className="col flex gap-3 interval-wrapper">
                    <div className="p-inputgroup w-full relative">
                      <InputText
                        id="interval"
                        className="task-card__field-input"
                        value={formatIntervalDisplay(selectedInterval)}
                        placeholder="Выберите интервал"
                        disabled={isFieldDisabled('interval')}
                        onClick={() => {
                          if (!isFieldDisabled('interval')) {
                            setShowIntervalDialog(true);
                          }
                        }}
                        readOnly
                        style={{ borderRadius: 6 }}
                      />
                      {selectedInterval && !isFieldDisabled('interval') && (
                        <Button
                          icon="pi pi-times"
                          className="p-button-text p-button-sm interval-clear-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearInterval();
                          }}
                          type="button"
                          tooltip="Удалить интервал"
                          tooltipOptions={{ position: 'top' }}
                          severity="danger"
                          style={{ 
                            width: '42px', 
                            height: '42px', 
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            zIndex: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s ease'
                          }}
                        />
                      )}
                    </div>
                  </div>
                </TaskFieldRow>
              )}
            </div>

            <div className="col task-card__column flex flex-column">
              {!!taskId ? (
                <>
                  <TaskFieldRow
                    htmlFor="taskId"
                    labelVariant="col4"
                    spaced
                    label="№ наряда:"
                    hasError={!!errors.taskStatus}
                    errorMessage={(errors.taskStatus as any)?.message}
                  >
                    <InputText
                      className="col mr-2 ml-2 task-card__field-input"
                      value={taskId as string}
                      disabled
                    />
                  </TaskFieldRow>

                  {renderExternalId()}
                  <TaskFieldRow
                    htmlFor="taskStatus"
                    labelVariant="col4"
                    hasError={!!errors.taskStatus}
                    errorMessage={(errors.taskStatus as any)?.message}
                    label={
                      <>
                        Статус наряда:
                        {isClosed && (
                          <i
                            className="pi pi-exclamation-circle closing-status-tooltip"
                            style={{
                              fontSize: '1rem',
                              color: 'rgb(255 168 0)',
                              cursor: 'pointer',
                              marginLeft: 10
                            }}
                            data-pr-tooltip="Наряд находится в закрывающем статусе"
                          />
                        )}
                        <Tooltip target=".closing-status-tooltip" />
                      </>
                    }
                  >
                    <InputText
                      className="col mr-2 ml-2 task-card__field-input"
                      value={`${(watch('taskStatus') as TreeNode).label}. ${
                        closeCode?.taskClosingCode || ''
                      }`}
                      disabled
                    />
                  </TaskFieldRow>
                </>
              ) : (
                renderExternalId()
              )}

              <TaskFieldRow
                htmlFor="plannedStartDate"
                labelVariant="col4"
                label="Дата и время начала работы (План):"
                hasError={!!(errors as any).plannedStartDate}
                errorMessage={(errors as any).plannedStartDate?.message}
              >
                <div className="col">
                  <DateTimeInput
                    name="plannedStartDate"
                    initialValue={watch('plannedStartDate') || ''}
                    setValue={setValue}
                    errors={errors}
                    disabled={isFieldDisabled('plannedStartDate')}
                    onChangeHandler={(e, fieldName) =>
                      handlePlannedStartDateChange(e)
                    }
                    manualInput={false}
                  />
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="actualStartDate"
                labelVariant="col4"
                label="Дата и время начала работы (Факт):"
                hasError={!!errors.actualStartDate}
                errorMessage={
                  errors.actualStartDate?.message as React.ReactNode
                }
              >
                <div className="col">
                  <DateTimeInput
                    name="actualStartDate"
                    initialValue={watch('actualStartDate') || ''}
                    setValue={setValue}
                    errors={errors}
                    disabled={isFieldDisabled('actualStartDate')}
                    onChangeHandler={(e, fieldName) => handleStartDateChange(e)}
                    manualInput={false}
                  />
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="plannedEndDate"
                labelVariant="col4"
                label="Дата и время окончания работы (План):"
                hasError={!!errors.plannedEndDate}
                errorMessage={errors.plannedEndDate?.message as React.ReactNode}
              >
                <div className="col">
                  <DateTimeInput
                    name="plannedEndDate"
                    initialValue={watch('plannedEndDate') || ''}
                    setValue={setValue}
                    errors={errors}
                    disabled={isFieldDisabled('plannedEndDate')}
                    onChangeHandler={(e, fieldName) =>
                      handleEndDateChange(e, 'plannedEndDate')
                    }
                    manualInput={false}
                  />
                </div>
              </TaskFieldRow>

              <TaskFieldRow
                htmlFor="actualEndDate"
                labelVariant="col4"
                label="Дата и время окончания работы (Факт):"
                hasError={!!errors.actualEndDate}
                errorMessage={errors.actualEndDate?.message as React.ReactNode}
              >
                <div className="col">
                  <DateTimeInput
                    name="actualEndDate"
                    setValue={setValue}
                    errors={errors}
                    initialValue={watch('actualEndDate') || ''}
                    disabled={isFieldDisabled('actualEndDate')}
                    onChangeHandler={(e, fieldName) =>
                      handleEndDateChange(e, 'actualEndDate')
                    }
                    manualInput={false}
                  />
                </div>
              </TaskFieldRow>

              <IntervalPickerDialog
                visible={showIntervalDialog}
                onHide={() => setShowIntervalDialog(false)}
                onSelect={handleIntervalSelect}
                selectedInterval={selectedInterval}
                departmentId={department?.key}
                taskTypeId={taskType?.key}
                selectedEmployees={employees?.map(e => String(e.id)) || []}
                disabled={isFieldDisabled('interval')}
                handleClearInterval={handleClearInterval}
              />
            </div>
          </div>

          <AssignedExecutorsTags
            employees={employees}
            tagItems={executorNodes}
            deletedMarkSource={executorsAvailableForDeletedMark}
            onDelete={
              isFieldDisabled('employees') ? undefined : handleDeleteTag
            }
          />
          <div className="flex flex-column">
            <div className="grid flex justify-content-between align-items-center gap-3 task-card__description-row">
              <label className="task-card__label--narrow" htmlFor="priority">
                Описание:
              </label>
              <InputTextarea
                className="w-full"
                {...register('description', {
                  onChange: (e) => {
                    setValue('description', e.target.value, {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  }
                })}
                rows={2}
                cols={30}
                disabled={isFieldDisabled('description')}
              />
            </div>
            <p
              className={` card-errors ${
                errors.description ? 'text-red-500' : 'text-transparent'
              }`}
            >
              {errors.description ? errors.description.message : ' '}
            </p>
          </div>
          <Divider className="mt-0 mb-3" />
          <TaskActionsSection
            possibleStatusTransitions={possibleStatusTransitions}
            selectedAction={selectedAction}
            onActionClick={possibleStatusTransitionsHandle}
            onActionSelect={handleActionSelect}
          />
        </>
      )}
      <style>
        {`
        .interval-wrapper:hover .interval-clear-btn {
            opacity: 1 !important;
          }
        `}
      </style>
    </div>
  );
};